import { normalizeEstimate } from "./normalizeEstimate";
import { groupEstimateBySection } from "./groupEstimateBySection";
import { groupSectionGroupsBySourceType } from "./groupSectionGroupsBySourceType";

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function isInactive(row = {}) {
  return Boolean(
    row.inactive === true ||
      row.excluded === true ||
      row?.flags?.inactive === true ||
      row?.flags?.excluded === true ||
      row?.meta?.inactive === true ||
      row?.meta?.excluded === true
  );
}

function collectAllRows(normalized) {
  if (Array.isArray(normalized?.rows)) return normalized.rows;

  return [
    ...(Array.isArray(normalized?.materials) ? normalized.materials : []),
    ...(Array.isArray(normalized?.labour) ? normalized.labour : []),
    ...(Array.isArray(normalized?.extras) ? normalized.extras : []),
  ];
}

function getRawRowTotal(row) {
  if (row?.total !== undefined) return toNumber(row.total);

  const quantity = toNumber(row?.quantity ?? row?.qty ?? row?.hours);
  const unitPrice = toNumber(
    row?.unitPrice ?? row?.price ?? row?.rate ?? row?.hourlyRate
  );

  return quantity * unitPrice;
}

function getSectionTotal(rows = []) {
  return rows
    .filter((row) => !isInactive(row))
    .reduce((sum, row) => sum + getRawRowTotal(row), 0);
}

function extractReviewFlags(rows = []) {
  const flags = [];

  for (const row of rows) {
    if (row?.reviewRequired) {
      flags.push({
        rowId: row.id,
        message: row.reviewNote || row.label || row.name || "Row needs review",
        type: row.type || row.rowKind || "unknown",
      });
    }

    if (row?.materialLibraryMatch === null) {
      flags.push({
        rowId: row.id,
        message: `${row.label || row.name || "Material"} has no material library match`,
        type: "material",
      });
    }

    if (row?.labourRuleMatch === null) {
      flags.push({
        rowId: row.id,
        message: `${row.label || row.name || "Labour"} has no labour rule match`,
        type: "labour",
      });
    }

    if (isInactive(row)) {
      flags.push({
        rowId: row.id,
        message: `${row.label || row.name || "Row"} is inactive and removed from quote total`,
        type: "optional",
      });
    }
  }

  return flags;
}

function getRowType(row = {}) {
  return row.type || row.rowKind || row.kind || "";
}

function calculateSummary(rows = [], parsedData = {}) {
  const activeRows = rows.filter((row) => !isInactive(row));
  const inactiveRows = rows.filter(isInactive);

  const materialsTotal = activeRows
    .filter((row) => getRowType(row) === "material")
    .reduce((sum, row) => sum + getRawRowTotal(row), 0);

  const labourTotal = activeRows
    .filter((row) => getRowType(row) === "labour")
    .reduce((sum, row) => sum + getRawRowTotal(row), 0);

  const extrasTotal = activeRows
    .filter((row) => getRowType(row) === "extra")
    .reduce((sum, row) => sum + getRawRowTotal(row), 0);

  const inactiveTotal = inactiveRows.reduce(
    (sum, row) => sum + getRawRowTotal(row),
    0
  );

  const subtotal = materialsTotal + labourTotal + extrasTotal;
  const marginPercent = toNumber(parsedData?.marginPercent);
  const grandTotal = subtotal;

  return {
    materialsTotal,
    materialsSubtotal: materialsTotal,

    labourTotal,
    labourSubtotal: labourTotal,

    extrasTotal,
    extrasSubtotal: extrasTotal,

    inactiveTotal,
    inactiveRowCount: inactiveRows.length,

    // Backwards compatibility
    excludedTotal: inactiveTotal,
    excludedRowCount: inactiveRows.length,

    subtotal,
    grandTotal,
    marginPercent,
  };
}

function buildScopeSummary(parsedData = {}) {
  const highlights = [];

  if (parsedData?.projectType) {
    highlights.push(`Project type: ${parsedData.projectType}`);
  }

  if (Array.isArray(parsedData?.walls) && parsedData.walls.length) {
    highlights.push(`Walls: ${parsedData.walls.length}`);
  }

  if (Array.isArray(parsedData?.rooms) && parsedData.rooms.length) {
    highlights.push(`Rooms: ${parsedData.rooms.length}`);
  }

  if (Array.isArray(parsedData?.terraces) && parsedData.terraces.length) {
    highlights.push(`Terraces: ${parsedData.terraces.length}`);
  }

  if (Array.isArray(parsedData?.roofs) && parsedData.roofs.length) {
    highlights.push(`Roofs: ${parsedData.roofs.length}`);
  }

  if (Array.isArray(parsedData?.windows) && parsedData.windows.length) {
    highlights.push(`Windows: ${parsedData.windows.length}`);
  }

  if (Array.isArray(parsedData?.doors) && parsedData.doors.length) {
    highlights.push(`Doors: ${parsedData.doors.length}`);
  }

  return {
    projectType: parsedData?.projectType || "",
    highlights,
  };
}

export function buildEstimateViewModel(estimate, parsedData = {}) {
  const normalized = normalizeEstimate(estimate);
  const allRows = collectAllRows(normalized);
  const sections = groupEstimateBySection(normalized);

  const enrichedSections = sections.map((section) => {
    const rows = Array.isArray(section?.rows) ? section.rows : [];
    const groups = groupSectionGroupsBySourceType(rows);
    const inactiveRows = rows.filter(isInactive);
    const activeRows = rows.filter((row) => !isInactive(row));

    return {
      ...section,
      rows,
      groups,
      total: getSectionTotal(rows),
      rowCount: rows.length,

      activeRowCount: activeRows.length,
      inactiveRowCount: inactiveRows.length,
      inactiveRows,

      // Backwards compatibility
      excludedRowCount: inactiveRows.length,
      excludedRows: inactiveRows,
    };
  });

  const reviewFlags = extractReviewFlags(allRows);

  return {
    sections: enrichedSections,
    summary: calculateSummary(allRows, parsedData),
    review: {
      count: reviewFlags.length,
      flags: reviewFlags,
    },
    scope: buildScopeSummary(parsedData),
  };
}